'use client';

import { Button, Filter } from '@sprd/sprd-component-kit/v2';
import { useQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useState } from 'react';
import { useTranslations } from 'next-intl';
import { NestedCategoryFilter } from '@/types/assortment';
import { useStateHistory } from '@/hooks/useStateHistory';
import { FilterDrawer } from '@/components/Filters/FilterDrawer';
import { ActiveListFilter, ListFacetsAndCategories } from '@/components/Filters/listFilter';
import { useRouter } from '@/i18n/navigation';
import { FEATURE_SUSTAINABLE_ID } from '@/utility/constants';
import { getCategoriesById, getDepartmentById } from '@/utility/listPages';
import { Locale } from '@/utility/locale';
import { post } from '@/utility/requestHelpers/clientRequestHelpers';
import { getMPListPageHrefArgs } from '@/utility/urls';

type ProductListFacetsAndCategories = Omit<ListFacetsAndCategories, 'prices' | 'brand'>;
type MPFilterDrawerProps = {
  shopId: string;
  locale: Locale;
  filter: ProductListFacetsAndCategories;
  activeFilter: Omit<ActiveListFilter<ProductListPageSort>, 'maxPrice'>;
  categories: NestedCategoryFilter[];
};

export const MPFilterDrawer = ({
  shopId,
  locale,
  filter: initialFilter,
  activeFilter: initialActiveFilter,
  categories,
}: MPFilterDrawerProps) => {
  const t = useTranslations();
  const router = useRouter();

  const [open, setOpen] = useState(false);
  const [activeFilter, setNextFilter, setBackFilter, hasChanged] =
    useStateHistory<ActiveListFilter>(initialActiveFilter);

  const { data: filter, isError } = useQuery({
    queryKey: ['mp-filter', shopId, locale, activeFilter],
    queryFn: async ({ signal }) => {
      const filter = activeFilter;
      const { facets } = await post<{ facets: ProductListFacetsAndCategories }>(
        '/internal/api/marketplaceFilterFacets',
        {
          shopId,
          locale,
          filter: {
            category: filter?.category,
            labels: {
              GENDER: filter?.department ? [filter?.department] : undefined,
              BRAND: filter?.brand || undefined,
              FIT: filter?.fit || undefined,
              MATERIALS: filter?.materials ? [filter?.materials] : undefined,
              NECKLINE: filter?.neckline || undefined,
              REFINEMENT: filter?.refinement ? [filter?.refinement] : undefined,
              SLEEVE: filter?.sleeve || undefined,
              STYLE: filter?.style ? [filter?.style] : undefined,
              FEATURE: filter?.sustainable ? [FEATURE_SUSTAINABLE_ID] : undefined,
            },
            appearanceGroups: filter?.appearance || undefined,
            sizeGroups: filter?.size,
            safeSearch: filter?.safesearch,
            embroidery: filter?.embroidery,
            searchTerm: filter?.searchTerm,
          },
          sort: filter?.sort as ProductListPageSort,
        },
        { signal }
      );

      if (!facets) {
        throw new Error('[MP Filter Drawer]: No facets returned. Invalid filter combination');
      }

      return { ...facets, categories };
    },
    enabled: hasChanged,
    placeholderData: (previousData) => previousData,
    retry: false,
  });

  useEffect(() => {
    if (isError) setBackFilter();
  }, [isError, setBackFilter]);

  const applyFilter = useCallback(
    (newFilter: Partial<ProductListFacetsAndCategories>, newActiveFilter: ActiveListFilter) => {
      const {
        department: newDepartment,
        category: newCategory,
        searchTerm,
        isUserQuery,
        userQuery,
        ...searchParams
      } = newActiveFilter;

      const {
        appearance,
        brand,
        fit,
        materials,
        neckline,
        refinement,
        size,
        sleeve,
        style,
        sort,
        ...otherParams
      } = searchParams;

      const href = getMPListPageHrefArgs({
        departmentSlug: newDepartment
          ? getDepartmentById(newFilter.departments ?? [], newDepartment)?.slug
          : undefined,
        categorySlugs: newCategory
          ? getCategoriesById(newFilter.categories ?? [], newCategory).map(
              (category) => category.slug
            )
          : undefined,
        searchTerm: isUserQuery ? undefined : searchTerm,
        userQuery: isUserQuery ? userQuery : undefined,
        queryParams: {
          ...otherParams,
          sort: sort as ProductListPageSort,
          appearance: appearance,
          brand: brand,
          fit: fit || undefined,
          materials: materials ? [materials] : undefined,
          neckline: neckline || undefined,
          refinement: refinement ? [refinement] : undefined,
          size: size,
          sleeve: sleeve || undefined,
          style: style ? [style] : undefined,
        },
      });

      setOpen(false);
      router.push(href, { scroll: false });
    },
    [router]
  );

  const {
    size: selectedSize,
    appearance: selectedAppearance,
    fit: selectedFit,
    sleeve: selectedSleeve,
    neckline: selectedNeckline,
    embroidery: embroiderySelected,
    department: selectedDepartment,
  } = initialActiveFilter ?? {};

  const selectedFiltersCount = [
    selectedSize,
    selectedAppearance,
    selectedFit,
    selectedNeckline,
    selectedSleeve,
    embroiderySelected,
    selectedDepartment,
  ].filter(Boolean).length;

  const currentFilter = filter ?? initialFilter;

  return (
    <>
      <Button
        className="border-none text-sm font-semibold"
        variant="invert"
        onClick={() => setOpen(true)}
        startIcon={<Filter className="size-6" />}
        endIcon={
          selectedFiltersCount && (
            <span className="inline-flex size-4 items-center justify-center bg-black text-[10px] text-white">
              {selectedFiltersCount}
            </span>
          )
        }
      >
        {t('filter.addFilter')}
      </Button>
      <FilterDrawer
        open={open}
        filter={{ ...currentFilter, brand: undefined }}
        activeFilter={activeFilter}
        onFilterUpdate={setNextFilter}
        onApplyFilter={applyFilter}
      />
    </>
  );
};
